/* eslint-disable max-len */
/* eslint-disable no-extra-parens */
/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import parse from 'parse-json';
import { v4 as uuidv4 } from 'uuid';
import _, { isArray } from 'lodash';
import { Headings } from '../Header/Header';
import HeaderWrapper from '../Header/HeaderWrapper';
import {
    getColorPalette,
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
    parseCssToObj,
} from '../utils';
import { defaultChartButtons } from '../StockChart/defaultChartButtons';
import GrowthChart, { GrowthChartProps } from './GrowthChart';
import { mergeOptions } from './mergeOptions';
import { prepareData } from './prepareData';
import growthChartStyles, { themeGrowthChartStyles } from './GrowthChart.styles';

export interface highChartOptions {
    path?: string;
    value?: string;
    valueType?: string;
    functionToTransform?: string;
}
export interface DatePickerOptions {
    startDateLabel: string;
    endDateLabel: string;
    startDayAriaLabel: string;
    startMonthAriaLabel: string;
    startYearAriaLabel: string;
    endDayAriaLabel: string;
    endMonthAriaLabel: string;
    endYearAriaLabel: string;
}

export interface ZoneOption {
    year: string;
    color: string;
}

export interface toolTipCustomOptions {
    label: string;
    mappingkey: string;
    format: string;
    className: string;
    isValueKey: boolean;
    seriesNumber: number;
    posChangeClassName: string;
    negChangeClassName: string;
    hideFirstTooltip: boolean;
}

interface GrowthChartWrapperProps {
    options: highChartOptions[];
    dataKey: string;
    chartType: string;
    constructorType: string;
    chartTitles: Headings[];
    noDataMessage: string;
    growthChartAltText?: string;
    showChartRange: boolean;
    rangeButtons: string;
    toolTipValueFormat: string;
    toolTipDateFormat: string;
    isLimitToTenYears: boolean;
    showDifferentZone: boolean;
    zoneOptions: ZoneOption;
    functionToTransform: string;
    transformData?: boolean;
    typographyClass?: string;
    applyMaxDateValue?: boolean;
    applyCustomYaxisLabel?: boolean;
    yAxislabelFormat?: string;
    xAxislabelFormat?: string;
    isShowMultipleSeries?: boolean;
    useSimpleDataset?: boolean;
    yAxisValueOverride: boolean;
    multipleSeriesMaxLength?: number;
    multipleSeriesColors?: any;
    customTooltipOptions?: boolean;
    tooltipOptions?: toolTipCustomOptions[];
    defaultSelectedRange: number;
}

// Define your custom property for pdf and pptx
interface CustomChartOptions extends Highcharts.Options {
    chart?: Highcharts.ChartOptions & {
        formatYaxisValue?: string;
        formatXaxisValue?: string;
    };
}

export interface WidgetProperties
    extends commonTypes.WidgetPropertiesBase,
        GrowthChartWrapperProps,
        GrowthChartProps {}

const GrowthChartWrapper = (props: commonTypes.WidgetInstanceProps) => {
    const { documentType, documentMetadata } = commonHooks.useAppContext();
    const { locale } = documentMetadata;
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;

    const {
        styles,
        options,
        dataKey,
        chartTitles,
        chartType,
        noDataMessage,
        growthChartAltText,
        showTotalReturns,
        showChartRange,
        showInvestmentAmount,
        customTooltipOptions,
        tooltipOptions,
        yAxisValueOverride,
        ...rest
    } = widgetProperties || props;

    const i18n = commonHooks.useLocalization();

    const data: any = props.data;
    const theme = data?.themeSelected || props?.theme;
    const themeColors = parseCssToObj(data?.theme || '');

    const chartColors = {
        line: themeColors?.['--color-graph-l'],
        label: themeColors?.['--color-graph-lb'],
    };

    const totalReturnRef = useRef<any>(null);
    const chartRef = useRef<any>();
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [extremes, setExtremes] = useState<any>();
    const [xExtremes, setXExtremes] = useState<{ min: number; max: number }>();
    const [investmentAmount, setInvestmentAmount] = useState<number>(rest.initialGrowthAmount);
    const [selectedRange, setSelectedRange] = useState<number | undefined>(undefined);

    const chartButtons: string = rest.rangeButtons ? rest.rangeButtons : defaultChartButtons;

    const handleRangeSelect = (index: number) => {
        // set the selected range
        setSelectedRange(index);
    };

    // parse every single button item and resolve handlebars
    const _chartButtons =
        isArray(parse(chartButtons)) &&
        parse(chartButtons).map((button: any, index: number) => {
            for (const property in button) {
                if (typeof button?.[property] !== 'string') continue;

                button[property] = i18n.format(button[property], data);
            }
            button.events = {
                click: function () {
                    // pass button index to handleRangeSelect
                    // to set the selected range
                    handleRangeSelect(index);
                },
            };

            return button;
        });

    const growthChartId = uuidv4();

    const growthChartData: any = dataKey && JsonQuery(`${dataKey}`, { data }).value;

    let transformedData = {
        data,
        showDifferentZone: rest.showDifferentZone,
        zoneOptions: rest.zoneOptions,
        showUnderlyingFund: false,
        underlyingFundPath: '',
        options,
        growthChartData,
        growthChartAltText,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedProps =
            rest.transformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, rest.functionToTransform, {
                ...transformedData,
                JsonQuery,
            });

        if (transformedProps) {
            transformedData = { ...transformedData, ...transformedProps };
        }
    } catch (e) {
        console.log(e);
    }

    const preparedData = parse(
        JSON.stringify(
            dataKey
                ? prepareData(
                      transformedData.growthChartData,
                      rest.isLimitToTenYears,
                      data?.overview?.performanceResetDate,
                      investmentAmount,
                      rest.useSimpleDataset,
                      yAxisValueOverride
                  )
                : null
        )
    );

    // transform and chose which series to show
    const seriesData: any =
        _.isArray(preparedData) &&
        preparedData.map((series: any) => ({
            ...series,
            type: chartType,
            data: series.data,
        }));

    if (rest.isShowMultipleSeries && rest.multipleSeriesMaxLength && rest.multipleSeriesColors) {
        seriesData.forEach((series: any, index: number) => {
            if (rest.multipleSeriesMaxLength && index >= rest.multipleSeriesMaxLength) {
                series.visible = false;
            } else {
                series.color = rest.multipleSeriesColors[index];
            }
        });
    }

    let maxDate = 0;
    if (rest.applyMaxDateValue) {
        // Finding the maximum date (x value) using reduce
        maxDate = Math.max.apply(
            null,
            seriesData.map((item: { data: string | any[] }) => {
                const lastDataPoint = item.data[item.data.length - 1]; // Get the last [x, y] pair
                return lastDataPoint.x; // Extract the x value (assuming it represents a date)
            })
        );
    }

    let yValueFormatter = '';
    if (rest.applyCustomYaxisLabel) {
        yValueFormatter = rest.yAxislabelFormat || '';
    }

    const setTotalReturns = (investedAmount: number, currentPrice: number) => {
        const returns: any = (currentPrice - investedAmount) / investedAmount;
        const formatToPercentage = `${i18n.t(returns, 'pctMax1')}`;

        if (totalReturnRef?.current && formatToPercentage) {
            totalReturnRef.current.innerText = formatToPercentage || '';
        }
    };

    useEffect(() => {
        if (chartRef?.current && xExtremes) {
            chartRef.current.updateGrowthChart(xExtremes.min);
            setIsLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [xExtremes]);

    // default option for tooltip
    let tooltipHighchartOptions: any = {
        shared: true,
        followPointer: false,
        followTouchMove: false,
        positioner: function () {
            return { x: 0, y: 25 };
        },
        shadow: false,
        split: false,
        useHTML: true,
        formatter: function () {
            let pointFormat = '';
            const points = this.points;
            const utcDate = new Date(this.x).toUTCString();
            const x = i18n.t(
                new Date(
                    new Date(utcDate).getUTCFullYear(),
                    new Date(utcDate).getUTCMonth(),
                    new Date(utcDate).getUTCDate()
                ).toString(),
                rest.toolTipDateFormat || 'dateMed'
            );

            if (!points || !points.length) return false;

            const y: any = i18n.t(this.y as any, rest.toolTipValueFormat || 'curMax2');
            pointFormat = `
                <div class="custom-tootltip-container">
                    <span class="tooltip-value">
                        ${i18n.t('widgets_growthChart_toolTipLabel')}
                        ${y} ${i18n.t('widgets_growthChart_at')} ${x}
                    </span>
                </div>
            `;

            return pointFormat;
        },
    };

    // custom option for tooltip
    if (customTooltipOptions && tooltipOptions && isLoaded) {
        tooltipHighchartOptions = {
            useHTML: true,
            formatter: function (this: any) {
                const points = this.points;
                if (!points || !points.length) return false;

                // Added logic to compare intial processed point to match selected tooltip point
                const curPointDate = new Date(this.points[0].point.date);
                const utcTimestamp = Date.UTC(
                    curPointDate.getUTCFullYear(),
                    curPointDate.getUTCMonth(),
                    curPointDate.getUTCDate()
                );

                // check if it is showing all values
                const isAllData =
                    this.points[0].series.processedXData.length ===
                    this.points[0].series.data.length;

                // selects index based on values shown
                const minDateTimeStamp = this.points[0].series.processedXData[isAllData ? 0 : 1];
                const isIntitialPoint = utcTimestamp === minDateTimeStamp;

                let text = '';
                // map based on tooltip options
                text += `<div class="tooltip-options">`;
                tooltipOptions.forEach((option) => {
                    if (!(isIntitialPoint && option.hideFirstTooltip)) {
                        const seriesNumber = option.seriesNumber ? Number(option.seriesNumber) : 0;

                        text += `<div class="${option.className}">`;
                        text += `<span>${i18n.format(option.label || '', data)}</span>`;
                        const mappingValue = JsonQuery(option.mappingkey, {
                            data: this.points[seriesNumber].point,
                        })?.value;
                        text += `<span class=${
                            mappingValue <= 0
                                ? option.negChangeClassName
                                : option.posChangeClassName
                        }>${
                            option.format ? i18n.t(mappingValue, option.format) : mappingValue
                        }</span>`;
                        text += '</div>';
                    }
                });
                text += '</div>';

                return text;
            },
        };
    }

    let highChartOptions: CustomChartOptions = {
        accessibility: {
            enabled: true,
            keyboardNavigation: {
                enabled: true,
                order: ['rangeSelector', 'zoom', 'legend', 'series', 'container', 'chartMenu'],
            },
        },
        chart: {
            height: 400,
            spacingTop: 100,
            spacingBottom: 30,
            events: {
                load: function (this: any) {
                    if (rest.isLimitToTenYears) {
                        const chart = this;
                        const yAxis = chart.yAxis[0];
                        const yExtremes = yAxis.getExtremes();
                        yAxis.setExtremes(yExtremes.dataMin, yExtremes.dataMax);
                    }
                },
            },
            formatYaxisValue: yValueFormatter, // This is used to format the y-axis label for pdf and pptx
            formatXaxisValue: rest.xAxislabelFormat || '',
        },
        title: {
            text: '',
        },
        lang: {
            rangeSelectorZoom: '',
        },
        rangeSelector: {
            enabled: showChartRange,
            inputEnabled: false,
            selected: selectedRange,
            verticalAlign: 'top',
            buttons: _chartButtons,
            floating: true,
            buttonPosition: { align: 'left', x: -20, y: -70 },
            buttonTheme: {
                width: 38,
                height: 16,
                fill: !theme ? 'none' : 'var(--color-growth-chart-range-button-fill)',
                stroke: !theme ? 'darkgray' : 'var(--color-growth-chart-range-button-stroke)',
                'stroke-width': 1,
                style: {
                    fontSize: !theme ? '8pt' : 'var(--font-size-xx-small)',
                    fontFamily: !theme ? '' : 'var(--font-family-regular)',
                },
                states: {
                    hover: {
                        fill: !theme ? 'grey' : 'var(--color-growth-chart-range-button-hover)',
                    },
                    select: {
                        fill: !theme
                            ? 'darkgrey'
                            : 'var(--color-growth-chart-range-button-selected)',
                    },
                    disabled: {
                        stroke: !theme
                            ? 'lightgrey'
                            : 'var(--color-growth-chart-range-button-disabled)',
                    },
                },
            },
        },
        credits: { enabled: false },
        colors: getColorPalette(),
        xAxis: {
            max: maxDate > 0 ? maxDate : null,
            type: 'datetime',
            gridLineWidth: 2,
            lineWidth: 0,
            tickLength: 0,
            tickWidth: 0,
            // Enable to show one tick per year (ms * sec * min * hr * day = 1 yr)
            tickInterval: 1000 * 3600 * 24 * 365,
            units: [['year', [1]]],
            labels: {
                format: rest.xAxislabelFormat ? undefined : '{value: %Y}',
                formatter: rest.xAxislabelFormat
                    ? function () {
                          let formattedDate;

                          try {
                              const utcDate = new Date(this.value)?.toUTCString();
                              const x = i18n.t(
                                  new Date(
                                      new Date(utcDate)?.getUTCFullYear(),
                                      new Date(utcDate)?.getUTCMonth(),
                                      new Date(utcDate)?.getUTCDate()
                                  )?.toString(),
                                  rest.xAxislabelFormat
                              );

                              formattedDate = x;
                          } catch (e) {
                              formattedDate = `${this.value}`;
                          }

                          return formattedDate;
                      }
                    : undefined,
            },
            events: {
                setExtremes(e) {
                    if (!_.isEqual(xExtremes, { min: e?.min, max: e?.max })) {
                        setXExtremes({ min: e.min, max: e.max });
                    }
                },
                afterSetExtremes: function (this, event) {
                    const visiblePoints = this.series?.[0]?.points?.filter(
                        (p: any) => typeof p.y === 'number' && p.isInside
                    );
                    const currentValue = visiblePoints?.[visiblePoints?.length - 1]?.y;

                    if (currentValue) {
                        setTotalReturns(investmentAmount, currentValue);
                    }

                    setExtremes(event);
                },
            },
        },
        yAxis: {
            title: { text: i18n.t('widgets_growthChart_investmentValue') },
            gridLineWidth: 2,
            opposite: false,
            lineWidth: 0,
            tickLength: 0,
            tickWidth: 0,
            showLastLabel: false,
            labels: {
                align: 'center',
                y: 5,
                formatter: function (this: any) {
                    return yValueFormatter === ''
                        ? i18n.t(this.value, 'decLong0').toString().replace('CA', '')
                        : i18n.t(this.value, yValueFormatter).toString().replace('CA', '');
                },
            },
            events: {
                afterSetExtremes: function (this: any) {
                    const visiblePoints = this.series?.[0]?.points?.filter(
                        (p: any) => typeof p.y === 'number' && p.isInside
                    );
                    const currentValue = visiblePoints?.[visiblePoints?.length - 1]?.y;
                    if (currentValue) {
                        setTotalReturns(investmentAmount, currentValue);
                    }
                },
            },
        },
        navigator: {
            enabled: true,
            handles: {
                width: 10,
            },
            series: {
                color: !theme ? '' : 'var(--color-growth-chart-navigator)',
                fillColor: !theme ? '' : 'var(--color-growth-chart-navigator-fill)',
                fillOpacity: 0,
                zones: [
                    {
                        value: rest.showDifferentZone
                            ? new Date(
                                  i18n.format(transformedData?.zoneOptions?.year, data)
                              ).getTime()
                            : undefined,
                        color: transformedData?.zoneOptions?.color,
                        fillColor: transformedData?.zoneOptions?.color,
                    },
                ],
            },
            maskFill: 'transparent',
            xAxis: {
                gridLineWidth: 0,
                lineWidth: 0,
                tickLength: 0,
                tickWidth: 0,
                // Enable to show one tick per year (ms * sec * min * hr * day = 1 yr)
                tickInterval: 1000 * 3600 * 24 * 365,
                units: [['year', [1]]],
                labels: {
                    format: '{value: %Y}',
                    y: 15,
                },
            },
        },
        plotOptions: {
            line: {
                animation: false,
                enableMouseTracking: true,
            },
            area: {
                threshold: null,
                lineColor: !theme
                    ? chartColors.line
                        ? chartColors.line
                        : ''
                    : 'var(--color-growth-chart-line)',
                fillColor: !theme ? '' : 'var(--color-growth-chart-fill)',
            },
            series: {
                color: !theme
                    ? chartColors.line
                        ? chartColors.line
                        : ''
                    : 'var(--color-growth-chart-line)',
                dataLabels: {
                    enabled: true,
                    backgroundColor: chartColors.label || '',
                    padding: 0,
                    formatter(this: any) {
                        const visiblePoints = this.series.points.filter(
                            (p: any) => typeof p.y === 'number' && p.isInside
                        );

                        // eslint-disable-next-line max-len
                        if (
                            visiblePoints.length &&
                            this.x === visiblePoints[visiblePoints.length - 1].x
                        ) {
                            return i18n
                                .t(visiblePoints[visiblePoints.length - 1].y, 'cur')
                                .toString()
                                .replace('CA', '');
                        } else {
                            return null;
                        }
                    },
                },
                zoneAxis: 'x',
                zones: [
                    {
                        value: rest.showDifferentZone
                            ? new Date(
                                  i18n.format(transformedData?.zoneOptions?.year, data)
                              ).getTime()
                            : undefined,
                        color: transformedData?.zoneOptions?.color,
                    },
                ],
            },
        },
        scrollbar: {
            enabled: false,
        },
        tooltip: tooltipHighchartOptions,
        series: seriesData,
        legend: { enabled: seriesData?.length > 1 },
    };

    // The function would merge user options with defaults options
    highChartOptions = mergeOptions(
        transformedData.options,
        highChartOptions,
        { ...data, transformedData, seriesData },
        i18n,
        props.widgetInstanceId as string
    );

    // Todo: we can remove this once we make changes to all old instances
    // where we are using area.linColor property for chart line color
    if (highChartOptions.plotOptions && highChartOptions.plotOptions?.area?.lineColor) {
        highChartOptions.plotOptions = {
            ...highChartOptions.plotOptions,
            series: {
                ...highChartOptions.plotOptions.series,
                color: highChartOptions.plotOptions?.area.lineColor,
            },
        };
    }

    const isRenderGrowthChart = Array.isArray(seriesData) && seriesData?.length > 0;

    const _growthChartAltText = i18n.format(
        transformedData.growthChartAltText || '',
        transformedData.data
    );

    // Ref to make sure the effect runs only once for range selection
    const hasRangeEffectRun = useRef(false);

    useEffect(() => {
        // check if the chart is loaded and default range is a number
        // and effect hasn't run already
        if (
            chartRef?.current?.updateGrowthChart &&
            typeof rest.defaultSelectedRange === 'number' &&
            !hasRangeEffectRun.current
        ) {
            hasRangeEffectRun.current = true;
            setSelectedRange(rest?.defaultSelectedRange);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartRef?.current?.updateGrowthChart, rest?.defaultSelectedRange]);

    return (
        <>
            <div
                id={`${growthChartId}`}
                className={`js-growth-chart-tile ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${!theme ? growthChartStyles : themeGrowthChartStyles}${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
            >
                <HeaderWrapper headings={chartTitles} data={data} />
                {isRenderGrowthChart ? (
                    ['pdf', 'pptx'].includes(documentType) ? (
                        <div id={`${growthChartId}-growth-chart`} />
                    ) : (
                        <GrowthChart
                            {...rest}
                            langOptions={
                                transformedData?.options?.filter((d) => d.path === 'lang')?.[0]
                            }
                            ref={chartRef}
                            data={data}
                            extremes={extremes}
                            seriesData={seriesData}
                            highChartOptions={highChartOptions}
                            showInvestmentAmount={showInvestmentAmount}
                            showTotalReturns={showTotalReturns}
                            setTotalReturnRef={totalReturnRef}
                            setInvestmentAmount={setInvestmentAmount}
                            investmentAmount={investmentAmount}
                            altText={_growthChartAltText}
                            tooltipOptions={tooltipOptions}
                            selectedRange={selectedRange}
                            resetSelectedRange={() => setSelectedRange(undefined)}
                            yAxisValueOverride={yAxisValueOverride}
                        />
                    )
                ) : (
                    <p
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(noDataMessage, data),
                        }}
                    />
                )}
            </div>
            {['pdf', 'pptx'].includes(documentType) && (
                <>
                    <script
                        id={`${growthChartId}-chart-options`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(highChartOptions),
                        }}
                    ></script>
                    <script
                        id={`${growthChartId}-json-data`}
                        type="text/json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(seriesData) }}
                    ></script>
                    <script
                        id={`${growthChartId}-constructorType`}
                        type="text/json"
                        dangerouslySetInnerHTML={{ __html: JSON.stringify(rest?.constructorType) }}
                    ></script>
                    <script
                        id={`${growthChartId}-locale`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(locale),
                        }}
                    ></script>
                    <script
                        id={`${growthChartId}-alt-text`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(_growthChartAltText),
                        }}
                    ></script>
                    <script
                        id={`${growthChartId}-resource-strings`}
                        type="text/json"
                        dangerouslySetInnerHTML={{
                            __html: data?.resourceStrings,
                        }}
                    ></script>
                </>
            )}
        </>
    );
};

export default GrowthChartWrapper;
